import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, CopyButton, Field, Input, cx } from '../../components/ui';
import { ApiError, api, type Credential, type CredentialCreated } from '../../lib/api';
import { displayState } from '../../lib/credentialState';
import { usePageTitle } from '../../lib/usePageTitle';
import { PanelState } from './DashboardLayout';

export function Credentials() {
  usePageTitle('Credentials — WaveQA');

  const queryClient = useQueryClient();
  const [issued, setIssued] = useState<CredentialCreated | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isPending, error: loadError } = useQuery({
    queryKey: ['credentials'],
    queryFn: api.getCredentials,

    // This page shows state that changes from *outside* the browser. Running
    // `waveqa setup` in a terminal flips a credential from "Ready to use" to
    // "Active", and nothing in this tab is told. Every other query here only
    // changes in response to a mutation, which already invalidates it — so the
    // app-wide defaults (no refetch on focus, 60s stale time) are right
    // everywhere except here.
    //
    // Two triggers, because either one alone leaves a real case stale:
    // returning from the terminal usually means a focus event, but not when the
    // dashboard sits on a second monitor and never lost focus at all.
    refetchOnWindowFocus: true,
    staleTime: 0,

    // Poll only while something is actually waiting to change: a credential
    // that has never been used. Once every credential has been used or revoked,
    // nothing here moves without a mutation, and the polling stops on its own.
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.status === 'active' && !c.last_used_at)
        ? 10_000
        : false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['credentials'] });
    void queryClient.invalidateQueries({ queryKey: ['subscription'] });
  };

  const create = useMutation({
    mutationFn: () => api.createCredential(name.trim()),
    onSuccess: (result) => {
      setIssued(result);
      setCreating(false);
      setName('');
      setError(null);
      invalidate();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not create that credential.'),
  });

  const rotate = useMutation({
    mutationFn: (id: string) => api.rotateCredential(id),
    onSuccess: (result) => {
      setIssued(result);
      setError(null);
      invalidate();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not rotate that credential.'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeCredential(id, 'revoked from dashboard'),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not revoke that credential.'),
  });

  const rename = useMutation({
    mutationFn: ({ id, name: next }: { id: string; name: string }) =>
      api.updateCredential(id, next),
    onSuccess: () => {
      setError(null);
      setNotice(null);
      invalidate();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not rename that credential.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCredential(id),
    onSuccess: (result) => {
      setError(null);
      // A credential with billed runs is revoked, not erased. Saying "deleted"
      // either way would be a lie the user could only catch by reloading.
      setNotice(result.detail);
      invalidate();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not delete that credential.'),
  });

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Agent credentials</h2>
          <p className="mt-1 max-w-xl text-sm text-hull-400">
            Each machine running the WaveQA agent needs its own credential pair.
            The secret is shown once, when it is created or rotated.
          </p>
        </div>
        {!creating && !issued && (
          <Button onClick={() => setCreating(true)}>New credential</Button>
        )}
      </div>

      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="info">{notice}</Alert>}

      {issued && <SecretReveal issued={issued} onDismiss={() => setIssued(null)} />}

      {creating && (
        <Card className="mb-5">
          <h3 className="text-base font-semibold text-white">New credential</h3>
          <form
            className="mt-4"
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
          >
            <Field
              label="Name"
              htmlFor="cred-name"
              hint="Something that identifies the machine, e.g. 'build server' or 'laptop'."
            >
              <Input
                id="cred-name"
                required
                autoFocus
                maxLength={128}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" disabled={create.isPending || !name.trim()}>
                {create.isPending ? 'Creating…' : 'Create credential'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setCreating(false);
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <PanelState
        loading={isPending}
        error={loadError}
        empty={data?.length === 0 && !creating}
        emptyTitle="No credentials yet"
        emptyBody="Create one, then run `waveqa setup` on the machine that will run verifications."
        emptyAction={<Button onClick={() => setCreating(true)}>Create your first credential</Button>}
      >
        <ul className="space-y-3">
          {data?.map((credential) => (
            <CredentialRow
              key={credential.id}
              credential={credential}
              onRotate={() => rotate.mutate(credential.id)}
              onRevoke={() => revoke.mutate(credential.id)}
              onRename={(next) => rename.mutate({ id: credential.id, name: next })}
              onDelete={() => remove.mutate(credential.id)}
              busy={
                rotate.isPending ||
                revoke.isPending ||
                rename.isPending ||
                remove.isPending
              }
            />
          ))}
        </ul>
      </PanelState>
    </div>
  );
}

/**
 * The one screen that ever displays a client secret.
 *
 * It cannot be reopened, so it says so plainly and stays until dismissed —
 * auto-hiding it after a timer would lose the secret for anyone who looked away.
 */
function SecretReveal({
  issued,
  onDismiss,
}: {
  issued: CredentialCreated;
  onDismiss: () => void;
}) {
  return (
    <Card className="mb-5 border-surge-600/60 bg-surge-500/5">
      <h3 className="text-base font-semibold text-white">
        Copy your client secret now
      </h3>
      <p className="mt-2 text-sm text-hull-300">{issued.warning}</p>

      <dl className="mt-4 space-y-3">
        <div>
          <dt className="text-xs uppercase tracking-wider text-hull-400">Client ID</dt>
          <dd className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-hull-700 bg-hull-950 px-3 py-2 font-mono text-sm text-hull-200">
              {issued.credential.client_id}
            </code>
            <CopyButton value={issued.credential.client_id} label="Copy client ID" />
          </dd>
        </div>

        <div>
          <dt className="text-xs uppercase tracking-wider text-hull-400">Client secret</dt>
          <dd className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-surge-600/50 bg-hull-950 px-3 py-2 font-mono text-sm text-surge-300">
              {issued.client_secret}
            </code>
            <CopyButton value={issued.client_secret} label="Copy client secret" />
          </dd>
        </div>
      </dl>

      <div className="mt-5 flex items-center gap-3 border-t border-hull-700 pt-4">
        <Button variant="secondary" onClick={onDismiss}>
          I have copied it
        </Button>
        <p className="text-xs text-hull-400">
          WaveQA stores only a hash. If you lose this, rotate the credential.
        </p>
      </div>
    </Card>
  );
}

function CredentialRow({
  credential,
  onRotate,
  onRevoke,
  onRename,
  onDelete,
  busy,
}: {
  credential: Credential;
  onRotate: () => void;
  onRevoke: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(credential.name);
  const revoked = credential.status === 'revoked';
  const display = displayState(credential);

  return (
    <Card as="li" className={cx(revoked && 'opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {editing ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const next = draftName.trim();
                if (next && next !== credential.name) onRename(next);
                setEditing(false);
              }}
            >
              <Input
                aria-label={`Name for ${credential.name}`}
                autoFocus
                maxLength={128}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                className="max-w-xs"
              />
              <Button type="submit" disabled={busy || !draftName.trim()}>
                Save
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setDraftName(credential.name);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </form>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-white">{credential.name}</h3>
              <span
                className={cx(
                  'rounded-full border px-2 py-0.5 text-[11px]',
                  display.tone,
                )}
                title={display.hint}
              >
                {display.label}
              </span>
              <button
                type="button"
                onClick={() => {
                  setDraftName(credential.name);
                  setEditing(true);
                }}
                disabled={busy}
                className="text-xs text-surge-400 hover:text-surge-300 disabled:opacity-50"
              >
                Rename
              </button>
            </div>
          )}

          <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <Detail label="Client ID" value={credential.client_id} mono />
            <Detail label="Secret" value={`${credential.secret_prefix}…`} mono />
            <Detail label="Created" value={formatDate(credential.created_at)} />
            <Detail
              label="Last used"
              value={
                credential.last_used_at ? formatDate(credential.last_used_at) : 'Never used'
              }
            />
            {credential.last_used_from_ip && (
              <Detail label="Last used from" value={credential.last_used_from_ip} mono />
            )}
            {credential.machine_mac && (
              <Detail label="Machine" value={credential.machine_mac} mono />
            )}
            {credential.revoked_at && (
              <Detail label="Revoked" value={formatDate(credential.revoked_at)} />
            )}
          </dl>

          {credential.revoked_reason === 'rotated' && (
            <p className="mt-3 text-xs text-hull-400">
              Replaced by a rotation. This secret stopped working immediately, so
              any agent still holding it will have stopped.
            </p>
          )}
        </div>

        {/*
          Delete stays available after revocation - a revoked credential is
          still clutter on the dashboard, and "remove it at any time" is the
          whole point. Rotate and Revoke only make sense while it still works.
        */}
        <div className="flex shrink-0 gap-2">
          {!revoked && (
            <>
              <Button variant="secondary" onClick={onRotate} disabled={busy}>
                Rotate
              </Button>
              <Button variant="ghost" onClick={() => setConfirming(true)} disabled={busy}>
                Revoke
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={() => setDeleting(true)} disabled={busy}>
            Delete
          </Button>
        </div>
      </div>

      {confirming && (
        // Revocation is immediate and irreversible, so it asks first.
        <div className="mt-4 rounded-lg border border-fail-600/40 bg-fail-600/10 p-4">
          <p className="text-sm text-white">Revoke “{credential.name}”?</p>
          <p className="mt-1 text-sm text-hull-300">
            Any agent using this secret stops working immediately. This cannot be
            undone — you would need to create a new credential and reconfigure
            that machine.
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={() => {
                setConfirming(false);
                onRevoke();
              }}
              disabled={busy}
            >
              Yes, revoke it
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {deleting && (
        <div className="mt-4 rounded-lg border border-fail-600/40 bg-fail-600/10 p-4">
          <p className="text-sm text-white">Delete “{credential.name}”?</p>
          <p className="mt-1 text-sm text-hull-300">
            The secret stops working immediately and the credential leaves this
            list. If verification runs have been billed against it, the record is
            revoked and kept instead — usage history is evidence behind an
            invoice and is never discarded.
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={() => {
                setDeleting(false);
                onDelete();
              }}
              disabled={busy}
            >
              Yes, delete it
            </Button>
            <Button variant="secondary" onClick={() => setDeleting(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="shrink-0 text-hull-400">{label}</dt>
      <dd className={cx('min-w-0 truncate text-hull-200', mono && 'font-mono text-xs')}>
        {value}
      </dd>
    </div>
  );
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}
